import { useEffect, useState } from 'react';

import { getLocalTrack } from '../offline/gpsTrackStore';
import { getCachedRoadMeshEntries } from './usePrecacheRouteMesh';
import { useRoadRangesById } from './useRoadDirectionLabels';
import {
  pointsWithin,
  resolveClosingStretches,
  type RoadStretch,
  type TrackPoint,
} from '../utils/roadStretches';

type Pk = { km: number; meter: number };

/**
 * Por qué carreteras pasó el tramo que se está cerrando, resuelto desde el
 * rastro que el aparato ya tiene grabado.
 *
 * ── Por qué es un hook compartido y no un efecto dentro de un modal ────────
 *
 * Los CUATRO caminos que cierran un tramo tienen que registrar lo mismo, y
 * hasta hoy solo lo hacían tres: el modal de Finalizar, el de finalizar el
 * recorrido entero y el detector automático. El botón manual «Canvi de sentit»
 * se quedó fuera desde que existen los trozos (2026-08-17) — y no por una
 * decisión, sino porque el cálculo vivía copiado dentro de un modal y al
 * siguiente nadie se lo llevó. Con el cálculo en un sitio, un camino nuevo lo
 * hereda o se nota que no lo llama.
 *
 * La DECISIÓN sigue estando en `resolveClosingStretches`, que es puro y tiene
 * sus propios tests; esto es solo la fontanería asíncrona: leer el rastro,
 * acotarlo a la ventana del tramo y servir las mallas cacheadas.
 *
 * 🔴 Sin rastro legible NO se inventa nada: se sigue con la lista vacía y
 * `resolveClosingStretches` devolverá `null`, que es lo que deja el tramo como
 * mono-carretera de siempre. El reparto entre las dos vías lo termina el
 * backend al cerrar, y sin cobertura llega al sincronizar.
 */
export function useClosingRoadStretches({
  routeId,
  segmentRoadId,
  roadIds,
  segmentStartDate,
  segmentPkInit,
  pkEnd,
}: {
  routeId?: string | null;
  segmentRoadId?: string | null;
  roadIds?: string[] | null;
  segmentStartDate?: string | null;
  segmentPkInit?: Pk | null;
  /** El PK con el que se va a cerrar. `null` mientras no sea legible. */
  pkEnd?: Pk | null;
}): RoadStretch[] | null {
  const rangesById = useRoadRangesById(roadIds ?? undefined);
  const [stretches, setStretches] = useState<RoadStretch[] | null>(null);

  useEffect(() => {
    const vias = roadIds ?? [];
    // Con una sola carretera no hay nada que repartir.
    if (!segmentRoadId || vias.length < 2 || !routeId || !segmentPkInit) return;
    if (!pkEnd) {
      setStretches(null);
      return;
    }

    let cancelled = false;
    (async () => {
      let points: TrackPoint[] = [];
      try {
        points = pointsWithin(await getLocalTrack(routeId), segmentStartDate);
      } catch {
        // Sin rastro legible se sigue: no se inventa un reparto.
        points = [];
      }
      if (cancelled) return;
      setStretches(
        resolveClosingStretches({
          pk: pkEnd,
          pkInit: { km: segmentPkInit.km, meter: segmentPkInit.meter },
          segmentRoadId,
          roadIds: vias,
          rangesById,
          points,
          meshEntries: getCachedRoadMeshEntries(vias),
        })
      );
    })();
    return () => {
      cancelled = true;
    };
    // `pkEnd` se compara por valor: es un objeto nuevo en cada render del
    // formulario y meterlo tal cual relanzaría el efecto sin parar.
  }, [
    routeId,
    segmentRoadId,
    roadIds,
    segmentStartDate,
    segmentPkInit,
    pkEnd?.km,
    pkEnd?.meter,
    rangesById,
  ]);

  return stretches;
}

export default useClosingRoadStretches;
